/**
 * Alerta Global de Novos Pedidos
 *
 * Componente montado no MainLayout que exibe um overlay fixo
 * com os dados dos novos pedidos em qualquer página do sistema.
 *
 * Funciona de forma independente - escuta WebSocket diretamente
 * e chama a API para aceitar/recusar pedidos.
 */

import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNewOrderAlert } from '@hooks/useNewOrderAlert';
import { updateOrderStatus, getOrder } from '@services/ordersService';
import { NewOrderAlertPanel } from '@/pages/Orders/components/NewOrderAlertPanel';
import { OrderReceipt } from '@components/shared/OrderReceipt';
import type { Order, OrderStatus } from '@/types/order';

export function GlobalNewOrderAlert() {
  const { hasAlertingOrders, alertingOrders, dismissAll, dismissOrder, restoreOrder } = useNewOrderAlert();
  const [loadingReceiptOrderId, setLoadingReceiptOrderId] = useState<string | null>(null);
  const [receiptOrder, setReceiptOrder] = useState<Order | null>(null);

  const handleUpdateStatus = useCallback(async (orderId: string, status: OrderStatus, reason?: string) => {
    // Salva dados do pedido para rollback em caso de erro
    const orderBackup = alertingOrders.find(o => o.id === orderId);
    try {
      // Remove imediatamente do alerta (otimista) para feedback instantâneo
      dismissOrder(orderId);
      await updateOrderStatus(orderId, status, reason);
      if (status === 'CONFIRMED') {
        toast.success('Pedido aceito!');
      } else if (status === 'CANCELLED') {
        toast.success('Pedido recusado.');
      }
    } catch (err: any) {
      // Rollback: restaura o pedido no alerta se a API falhou
      if (orderBackup) {
        restoreOrder(orderId, orderBackup);
      }
      toast.error(err?.message || 'Erro ao atualizar status do pedido.');
    }
  }, [dismissOrder, restoreOrder, alertingOrders]);

  const handlePrintOrder = useCallback(async (order: Order) => {
    setLoadingReceiptOrderId(order.id);
    try {
      const fullOrder = await getOrder(order.id);
      setReceiptOrder(fullOrder);
    } catch {
      toast.error('Erro ao carregar detalhes do pedido para impressão.');
    } finally {
      setLoadingReceiptOrderId(null);
    }
  }, []);

  if (!hasAlertingOrders) return receiptOrder ? (
    <OrderReceipt
      order={receiptOrder}
      isOpen={!!receiptOrder}
      onClose={() => setReceiptOrder(null)}
    />
  ) : null;

  return (
    <>
      {/* Overlay fixo no topo da tela - acima de todo o conteúdo */}
      <div className="fixed inset-x-0 top-0 z-[100] max-h-[85vh] overflow-y-auto bg-black/30 p-4 backdrop-blur-sm">
        <div className="mx-auto max-w-2xl">
          <NewOrderAlertPanel
            orders={alertingOrders}
            onUpdateStatus={handleUpdateStatus}
            onPrintOrder={handlePrintOrder}
            onDismissAll={dismissAll}
            loadingReceiptOrderId={loadingReceiptOrderId}
          />
        </div>
      </div>

      {/* Modal de impressão */}
      {receiptOrder && (
        <OrderReceipt
          order={receiptOrder}
          isOpen={!!receiptOrder}
          onClose={() => setReceiptOrder(null)}
        />
      )}
    </>
  );
}
